import { useI18nContext } from '@/i18n/i18n-react';
import { useCallback, useEffect, useRef } from 'react';
import { useBlocker, useNavigate } from 'react-router';

/**
 * Similar to react router's useBlocker, but also blocks hard reloads / tab close / full navigations (incl. cross-origin).
 * @returns an object with a `forceNavigate` function that can be used to navigate without blocking.
 */
export function useBlockerFull(isBlocking: boolean) {
  const { LL } = useI18nContext();
  const navigate = useNavigate();

  const enabled = useRef(true);

  // Block SPA navigations
  useBlocker(() => {
    return (
      enabled.current &&
      isBlocking &&
      !confirm(LL.common.unsavedChangesPrompt())
    );
  });

  // Block hard reloads / tab close / full navigations (incl. cross-origin)
  useEffect(() => {
    if (!isBlocking) return;

    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!enabled.current) {
        return;
      }

      // Browser will show its own generic prompt; custom text is ignored.
      e.preventDefault();
      e.returnValue = ''; // required by some browsers
      return '';
    };

    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isBlocking]);

  const forceNavigate: typeof navigate = useCallback(
    (...args) => {
      enabled.current = false;
      // @ts-expect-error this works, but typescript complains because navigate is an overloaded function
      const result = navigate(...args);
      enabled.current = true;
      return result;
    },
    [navigate]
  );

  return {
    forceNavigate,
  };
}
